import { useMemo, useState } from 'react';

import { Combobox } from '~/shadcn/components/ui/Combobox';
import { Label } from '~/shadcn/components/ui/Label';
import { Input } from '~/shadcn/components/ui/Input';
import { Textarea } from '~/shadcn/components/ui/Textarea';

import { productionApi } from '~/api/production';

import CarouselLink from '../../components/CarouselLink';
import ColorInput from '../../components/ColorInput';
import ImageInput from '../../components/ImageInput';
import SlugDisplay from '../../components/SlugDisplay';
import { useFormControls } from '../../providers/FormStateProvider';

const DISPLAY_NAME = 'Product Spotlight';

/**
 * Default configuration for the ProductSpotlight slice component
 *
 * @constant {Object}
 * @property {string} imageUrl - URL for the stored image
 * @property {string} name - Name for the spotlight
 * @property {string} description - Description text for the spotlight
 * @property {string} linkUrl - URL for the spotlight link
 * @property {string} linkText - Text for the spotlight link
 * @property {string} items - Array of length 1 containing the ID of the selected product
 * @property {string} bgColor - Background color value
 */
const DEFAULT_SLICE = Object.freeze({
    imageUrl: '',
    name: '',
    description: '',
    linkUrl: '',
    linkText: '',
    items: [],
    bgColor: '#FFFFFF',
});

/**
 * Product Spotlight slice component
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {Function} props.onChange - Callback function for handling slice changes
 * @param {Object} props.slice - Slice configuration object
 * @returns {JSX.Element} ProductSpotlight component
 */
function ProductSpotlight({ disabled, onChange, slice }) {
    const { sellableBundles, sellableItems } = useFormControls();

    const [imageError, setImageError] = useState(null);

    const handleChange = (e) => {
        onChange({ [e.target.name]: e.target.value });
    };

    const handleImageUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            onChange({ imageUrl });
            setImageError(null);
        } catch (e) {
            setImageError(`Error: ${e.message}`);
        }
    };

    const handleClearImage = () => {
        onChange({ imageUrl: '' });
    };

    const sellableProducts = useMemo(
        () => [...(sellableBundles ?? []), ...(sellableItems ?? [])],
        [sellableBundles, sellableItems],
    );

    const handleProductChange = (externalSku) => {
        const product = sellableProducts.find((p) => p.externalSku === externalSku);
        onChange({ items: [product] });
    };

    const options = useMemo(
        () =>
            sellableProducts.map((item) => ({
                value: item.externalSku,
                label: `${item.externalSku}: ${item.name}`,
            })) ?? [],
        [sellableBundles, sellableItems],
    );

    return (
        <div className="flex flex-col gap-4">
            {slice.carouselID && <CarouselLink carouselID={slice.carouselID} />}

            <ImageInput
                id={`image-${slice.id}`}
                imageUrl={slice.imageUrl}
                error={imageError}
                onClearImage={handleClearImage}
                onImageUpload={handleImageUpload}
                disabled={disabled}
            />

            <div className="flex flex-col gap-2">
                <div className="flex gap-1 justify-between">
                    <Label htmlFor={`name-${slice.id}`}>Name</Label>
                    <SlugDisplay name={slice.name} sliceId={slice.id} onChange={onChange} />
                </div>
                <Input
                    type="text"
                    id={`name-${slice.id}`}
                    name="name"
                    value={slice.name ?? ''}
                    placeholder="Enter name"
                    onChange={handleChange}
                    required
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`description-${slice.id}`}>Description</Label>
                <Textarea
                    id={`description-${slice.id}`}
                    name="description"
                    value={slice.description ?? ''}
                    placeholder="Enter description"
                    onChange={handleChange}
                    rows="3"
                    disabled={disabled}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                    <Label htmlFor={`linkText-${slice.id}`}>Link Text</Label>
                    <Input
                        type="text"
                        id={`linkText-${slice.id}`}
                        name="linkText"
                        value={slice.linkText ?? ''}
                        placeholder="Enter link text"
                        onChange={handleChange}
                        disabled={disabled}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <Label htmlFor={`linkUrl-${slice.id}`}>Link URL</Label>
                    <Input
                        type="text"
                        id={`linkUrl-${slice.id}`}
                        name="linkUrl"
                        value={slice.linkUrl ?? ''}
                        placeholder="Enter link URL"
                        onChange={handleChange}
                        disabled={disabled}
                        pattern="^(https:\/\/([a-zA-Z0-9]+\.)?(misfitsmarket\.com|imperfectfoods\.com)|\/(shop|account))(\/.*)?$"
                        title="Must be a URL starting with https://misfitsmarket.com or https://imperfectfoods.com (subdomains allowed), or /shop or /account"
                    />
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`productID-${slice.id}`}>Product</Label>
                <Combobox
                    handleSelect={handleProductChange}
                    hideCheckmark
                    id={`productID-${slice.id}`}
                    options={options}
                    value={slice.items[0]?.external_sku || slice.items[0]?.externalSku}
                    disabled={disabled}
                    commandProps={{
                        filter: (value, search) => value.toLowerCase().includes(search.toLowerCase()),
                    }}
                />
            </div>

            <ColorInput
                id={`bgColor-${slice.id}`}
                label="Background Color"
                name="bgColor"
                value={slice.bgColor}
                onChange={handleChange}
                disabled={disabled}
            />
        </div>
    );
}

const validateSlice = (slice, sliceIndex) => {
    const errors = [];

    if (!slice.imageUrl) {
        errors.push(`${DISPLAY_NAME} (position ${sliceIndex}) requires an image.`);
    }

    if (slice.linkText && !slice.linkUrl) {
        errors.push(`${DISPLAY_NAME} (position ${sliceIndex}) requires a link URL if link text is provided.`);
    }

    if (!slice.linkText && slice.linkUrl) {
        errors.push(`${DISPLAY_NAME} (position ${sliceIndex}) requires link text if a link URL is provided.`);
    }

    if (!slice.items.length) {
        errors.push(`${DISPLAY_NAME} (position ${sliceIndex}) requires a product.`);
    }

    return errors;
};

export default Object.assign(ProductSpotlight, {
    category: 'other',
    defaultSlice: DEFAULT_SLICE,
    description: 'A carousel consisting of clickable banners containing an image, name, description, and link.',
    displayName: DISPLAY_NAME,
    thumbnail: 'https://img-cdn.misfitsmarket.com/test/lettuce/Minimap-Thumbnail__Product-Spotlight__Desktop',
    displayType: 'PRODUCT_SPOTLIGHT',
    validateSlice,
});
